import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import { type MouseEvent, useCallback } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { TruncatedContent } from "../TruncatedContent";
import type { TagProps, TagSize } from "./types";

const tagVariants = cva(
  "inline-flex items-center justify-center rounded-lg px-2 gap-1 w-fit focus-visible:outline",
  {
    variants: {
      color: {
        default:
          "bg-neutral-100 text-neutral-800 focus-visible:outline-neutral-800",
        warning:
          "bg-warning-100 text-warning-800 focus-visible:outline-warning-800",
        success:
          "bg-success-100 text-success-800 focus-visible:outline-success-800",
        danger:
          "bg-danger-100 text-danger-800 focus-visible:outline-danger-800",
        primary:
          "bg-neutral-100 text-neutral-800 focus-visible:outline-neutral-800",
        neutral:
          "bg-neutral-500/5 text-neutral-500/50 focus-visible:outline-neutral-500/50",
        info: "bg-info-100 text-info-800 focus-visible:outline-info-800",
      },
      size: {
        sm: "h-5 text-xs font-medium focus-visible:outline-1",
        md: "h-6 text-sm font-medium focus-visible:outline-2",
        lg: "h-8 text-md font-medium focus-visible:outline-2",
      },
    },
    defaultVariants: {
      color: "default",
      size: "md",
    },
  },
);

const Tag = ({
  className,
  color,
  size = "md",
  children,
  label,
  icon,
  id,
  onRemove,
  textClassName,
  disabled,
  ...props
}: TagProps) => {
  const { t } = useTranslation();

  const rootClass = cn(
    tagVariants({ color, size }),
    props.onClick && "cursor-pointer",
    "flex items-center overflow-hidden",
    className,
  );

  const contentMap: Record<TagSize, string> = {
    sm: cn("text-xs font-medium", textClassName),
    md: cn("text-sm font-medium", textClassName),
    lg: cn("text-md font-medium", textClassName),
  };

  const onTagRemove = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      onRemove?.(id);
    },
    [onRemove, id],
  );

  return (
    <div
      {...props}
      id={id}
      className={rootClass}
      tabIndex={props.onClick ? 0 : undefined}
      role={props.onClick ? "button" : undefined}
    >
      {icon}
      <TruncatedContent className={contentMap[size]}>{label}</TruncatedContent>
      {!disabled && onRemove && (
        <IconButton
          size="xs"
          variant="ghost"
          color="neutral"
          className={cn(
            "w-[14px] h-[14px] bg-transparent hover:bg-neutral-500/5 focus:bg-neutral-500/5 rounded-[4px] p-0",
            size === "lg" && "w-4 h-4 ms-0.5",
          )}
          onClick={onTagRemove}
          aria-label={t("tag.remove")}
        >
          <XIcon size={12} />
        </IconButton>
      )}
    </div>
  );
};

export { Tag, tagVariants };
